const MD5_SHIFTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
  20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6,
  10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

const MD5_CONSTANTS = new Uint32Array(64);
for (let i = 0; i < 64; i++) {
  MD5_CONSTANTS[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0;
}

const toUint8Array = (input) => {
  if (typeof input === "string") {
    return new TextEncoder().encode(input);
  }
  if (input instanceof Uint8Array) {
    return input;
  }
  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  }
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }
  throw new TypeError("Unsupported input type for createHash update.");
};

const leftRotate = (value, shift) => ((value << shift) | (value >>> (32 - shift))) >>> 0;

const add32 = (a, b) => (a + b) >>> 0;

const md5 = (bytes) => {
  const originalLength = bytes.length;
  const bitLength = originalLength * 8;
  const paddedLength = (((originalLength + 8) >>> 6) << 6) + 64;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(bytes);
  buffer[originalLength] = 0x80;

  const view = new DataView(buffer.buffer);
  view.setUint32(paddedLength - 8, bitLength >>> 0, true);
  view.setUint32(paddedLength - 4, Math.floor(bitLength / 0x100000000), true);

  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  for (let offset = 0; offset < paddedLength; offset += 64) {
    const M = new Uint32Array(16);
    for (let i = 0; i < 16; i++) {
      M[i] = view.getUint32(offset + i * 4, true);
    }

    let A = a;
    let B = b;
    let C = c;
    let D = d;

    for (let i = 0; i < 64; i++) {
      let F;
      let g;

      if (i < 16) {
        F = (B & C) | (~B & D);
        g = i;
      } else if (i < 32) {
        F = (D & B) | (~D & C);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        F = B ^ C ^ D;
        g = (3 * i + 5) % 16;
      } else {
        F = C ^ (B | ~D);
        g = (7 * i) % 16;
      }

      const temp = D;
      D = C;
      C = B;
      const sum = add32(add32(add32(A, F >>> 0), MD5_CONSTANTS[i]), M[g]);
      B = add32(B, leftRotate(sum, MD5_SHIFTS[i]));
      A = temp;
    }

    a = add32(a, A);
    b = add32(b, B);
    c = add32(c, C);
    d = add32(d, D);
  }

  const result = new Uint8Array(16);
  const resultView = new DataView(result.buffer);
  resultView.setUint32(0, a, true);
  resultView.setUint32(4, b, true);
  resultView.setUint32(8, c, true);
  resultView.setUint32(12, d, true);
  return result;
};

export function createHash(algorithm) {
  const normalizedAlgorithm =
    typeof algorithm === "string" ? algorithm.toLowerCase().replace("-", "") : "";
  if (normalizedAlgorithm === "md5") {
    const data = [];
    return {
      update(input) {
        data.push(toUint8Array(input));
        return this;
      },
      digest(encoding) {
        const totalLength = data.reduce((sum, chunk) => sum + chunk.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of data) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }
        const hashBytes = md5(combined);
        if (encoding === "hex") {
          return Array.from(hashBytes, (b) => b.toString(16).padStart(2, "0")).join("");
        }
        return hashBytes.buffer;
      },
    };
  }
  // Minimal SHA-256 polyfill using Web Crypto API
  if (
    normalizedAlgorithm === "sha256" &&
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.subtle
  ) {
    let data = [];
    return {
      update(input) {
        if (typeof input === "string") {
          data.push(new TextEncoder().encode(input));
        } else {
          data.push(input);
        }
        return this;
      },
      async digest(encoding) {
        const buffer = await window.crypto.subtle.digest(
          "SHA-256",
          data.length === 1
            ? data[0]
            : new Uint8Array(data.reduce((acc, curr) => [...acc, ...curr], []))
        );
        if (encoding === "hex") {
          return Array.from(new Uint8Array(buffer))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
        }
        return buffer;
      },
    };
  }
  throw new Error("No suitable createHash implementation found.");
}
